import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ShoppingBag } from 'lucide-react';

export function OrderTypeChart({ data }) {
  // Process the data to extract order types
  const processOrderTypes = (inputData) => {
    // Default empty array
    const defaultData = [];

    if (!inputData) return defaultData;

    // Case 1: If data is already in the expected format with name/orders/avgOrder
    if (Array.isArray(inputData) && inputData.length > 0) {
      // Check if it's already in the right format
      if (inputData[0].name !== undefined && inputData[0].orders !== undefined) {
        return inputData;
      }
      
      // Try to map from different formats
      return inputData.map(item => ({
        name: item.OrderMode || item.mode || item.name || 'Unknown',
        orders: item.OrderCount || item.count || item.orders || 0,
        avgOrder: item.AverageSales || item.average || item.avgOrder || 
                 (item.TotalNetSales && item.OrderCount ? 
                  (item.TotalNetSales / item.OrderCount) : 0)
      }));
    }

    // Case 2: If data is an object with orderModes property
    if (inputData.orderModes && Array.isArray(inputData.orderModes)) {
      return inputData.orderModes.map(item => ({
        name: item.OrderMode || item.mode || item.name || 'Unknown',
        orders: item.OrderCount || item.count || item.orders || 0,
        avgOrder: item.AverageSales || item.average || item.avgOrder ||
                 (item.TotalNetSales && item.OrderCount ? 
                  (item.TotalNetSales / item.OrderCount) : 0)
      }));
    }

    // Case 3: If data is Table11 directly (from your API)
    if (Array.isArray(inputData) && inputData[0]?.OrderMode) {
      return inputData.map(item => ({
        name: item.OrderMode || 'Unknown',
        orders: item.OrderCount || 0,
        avgOrder: item.AverageSales || 
                 (item.TotalNetSales && item.OrderCount ? 
                  (item.TotalNetSales / item.OrderCount) : 0)
      }));
    }

    return defaultData;
  };

  const processedData = processOrderTypes(data);
  
  // If no data after processing, show empty state
  if (processedData.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Order Types</h3>
            <p className="text-sm text-slate-500 mt-1">Performance by service type</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg">
            <ShoppingBag className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="h-[300px] flex items-center justify-center">
          <div className="text-center">
            <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No order type data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate additional metrics for display
  const totalOrders = processedData.reduce((sum, item) => sum + item.orders, 0);
  const totalRevenue = processedData.reduce((sum, item) => sum + (item.avgOrder * item.orders), 0);
  const overallAvg = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Order Types</h3>
          <p className="text-sm text-slate-500 mt-1">Performance by service type</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg">
          <ShoppingBag className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="mb-4 p-3 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Overall Average Order Value</span>
          <span className="text-lg font-bold text-purple-700">
            Rs {overallAvg.toFixed(2)}
          </span>
        </div>
      </div>

      <div style={{ width: '100%', height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={processedData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                padding: '8px 12px'
              }}
              formatter={(value, name) => {
                if (name === 'Orders') return [value, 'Orders'];
                return [`Rs ${value.toFixed(2)}`, 'Avg Order'];
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="circle"
            />
            <Bar 
              yAxisId="left"
              dataKey="orders" 
              fill="#8b5cf6" 
              radius={[8, 8, 0, 0]} 
              name="Orders" 
            />
            <Bar 
              yAxisId="right"
              dataKey="avgOrder" 
              fill="#01796d" 
              radius={[8, 8, 0, 0]} 
              name="Avg Order Value" 
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        {processedData.map((item, index) => (
          <div key={index} className="p-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-purple-200 transition-all">
            <p className="text-xs text-slate-500 font-medium mb-1">{item.name}</p>
            <p className="text-lg font-bold text-slate-900">{item.orders}</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-[#01796d] font-semibold">
                Rs {item.avgOrder.toFixed(2)}
              </p>
              <p className="text-xs text-slate-400">
                {totalOrders > 0 ? ((item.orders / totalOrders) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}